import { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import Layout from "@/components/Layout";
import { toast } from "sonner";
import { FileBarChart2, Gift, Users, Table2, BadgeCheck, Copy, Trash2, Send, Download } from "lucide-react";

const dl = async (path, name) => {
  const res = await api.get(path, { responseType: "blob" });
  const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = name; a.click(); URL.revokeObjectURL(url);
};
const copy = (t) => { navigator.clipboard?.writeText(t); toast.success("Copied"); };

export default function Tools() {
  const [ref, setRef] = useState(null);
  const [collabs, setCollabs] = useState([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("viewer");
  const [badge, setBadge] = useState(null);
  const load = () => {
    api.get("/referrals").then((r) => setRef(r.data)).catch(() => {});
    api.get("/collaborators").then((r) => setCollabs(r.data)).catch(() => {});
    api.get("/badge/link").then((r) => setBadge(r.data.token)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const origin = window.location.origin;

  const invite = async () => {
    if (!email) return toast.error("Enter an email");
    await api.post("/collaborators", { email, role }); toast.success("Invitation sent"); setEmail(""); load();
  };
  const sendReport = async () => {
    const { data } = await api.post("/reports/monthly/send-now");
    data.ok ? toast.success(`Health report emailed (score ${data.score})`) : toast.error(data.detail || "Could not send");
  };

  return (
    <Layout>
      <h1 className="font-heading text-2xl sm:text-3xl font-bold mb-1">Tools</h1>
      <p className="text-sm text-muted-foreground mb-6">Reports, sharing and the extras that make GetCerty pay for itself.</p>
      <div className="grid lg:grid-cols-2 gap-5">
        <Card icon={FileBarChart2} title="Monthly health report" body="A one-page branded PDF: portfolio score, every property, and what's coming up in 60 days. Emailed on the 1st of each month.">
          <div className="flex flex-wrap gap-2">
            <button data-testid="download-health-report" onClick={() => dl("/reports/monthly.pdf", "getcerty-health.pdf")} className="btn-primary"><Download size={16} /> Download PDF</button>
            <button data-testid="email-health-report" onClick={sendReport} className="btn-outline"><Send size={16} /> Email me now</button>
          </div>
        </Card>
        <Card icon={Table2} title="Portfolio export (CSV)" body="Every property, requirement, date and issuer in a spreadsheet — perfect for your accountant or a lender.">
          <button data-testid="download-csv" onClick={() => dl("/export/portfolio.csv", "getcerty-portfolio.csv")} className="btn-primary"><Download size={16} /> Download CSV</button>
        </Card>
        <Card icon={Gift} title="Refer a landlord, get a month free" body="When someone you refer subscribes, a month's credit is applied to your next invoice automatically.">
          {ref && (<>
            <button data-testid="referral-link" onClick={() => copy(`${origin}/?ref=${ref.code}`)} className="w-full text-left flex items-center gap-2 text-xs font-mono bg-secondary rounded-lg px-3 py-2"><span className="truncate flex-1">{origin}/?ref={ref.code}</span><Copy size={14} /></button>
            <p className="text-xs text-muted-foreground mt-2" data-testid="referral-stats">{ref.referred.length} referred · {ref.referred.filter((r) => r.subscribed).length} subscribed · {ref.credits} month{ref.credits === 1 ? "" : "s"} earned</p>
          </>)}
        </Card>
        <Card icon={BadgeCheck} title="Compliant landlord badge" body="A live, verifiable badge for listings and tenant trust. Links to a public page showing your portfolio status — never your documents.">
          {badge && (<>
            <img src={`${API}/badge/${badge}.svg`} alt="GetCerty verified badge" className="h-11" data-testid="badge-img" />
            <div className="mt-2 flex flex-wrap gap-2">
              <button data-testid="copy-badge-link" onClick={() => copy(`${origin}/verify/${badge}`)} className="btn-outline text-xs"><Copy size={14} /> Copy verify link</button>
              <button data-testid="copy-badge-html" onClick={() => copy(`<a href="${origin}/verify/${badge}"><img src="${API}/badge/${badge}.svg" alt="GetCerty verified landlord"></a>`)} className="btn-outline text-xs"><Copy size={14} /> Copy embed HTML</button>
            </div>
          </>)}
        </Card>
        <Card icon={Users} title="Letting agent / accountant access" body="Viewers see your records. Managers can also upload certificates, file documents and log repairs on your behalf — but never touch billing, tenancies or account settings." wide>
          <div className="flex flex-col sm:flex-row gap-2">
            <input data-testid="collab-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="agent@example.co.uk" className="flex-1 min-h-[44px] px-3 rounded-xl border border-input bg-card" />
            <select data-testid="collab-role" value={role} onChange={(e) => setRole(e.target.value)} className="min-h-[44px] px-3 rounded-xl border border-input bg-card"><option value="viewer">View only</option><option value="manager">Manage (upload & log repairs)</option></select>
            <button data-testid="collab-invite" onClick={invite} className="btn-primary">Invite</button>
          </div>
          <div className="mt-3 space-y-2">
            {collabs.map((c) => <div key={c.id} data-testid={`collab-${c.id}`} className="flex items-center justify-between text-sm p-3 rounded-xl border border-border"><span>{c.email} <span className={`text-[10px] px-1.5 py-0.5 rounded ml-1 ${c.role === "manager" ? "bg-emerald-100 text-emerald-800" : "bg-secondary text-muted-foreground"}`}>{c.role === "manager" ? "manager" : "viewer"}</span></span><button data-testid={`collab-del-${c.id}`} onClick={async () => { await api.delete(`/collaborators/${c.id}`); load(); }} className="text-rose-600 p-1"><Trash2 size={15} /></button></div>)}
            {collabs.length === 0 && <p className="text-xs text-muted-foreground">No one has access yet.</p>}
          </div>
        </Card>
      </div>
      <style>{`.btn-primary{min-height:44px;padding:0 1rem;display:inline-flex;align-items:center;gap:.5rem;border-radius:.75rem;background:#0F172A;color:#fff;font-weight:600}.btn-outline{min-height:44px;padding:0 1rem;display:inline-flex;align-items:center;gap:.5rem;border-radius:.75rem;border:1px solid hsl(var(--border));font-weight:500}`}</style>
    </Layout>
  );
}

function Card({ icon: Icon, title, body, children, wide }) {
  return (
    <div className={`bg-card border border-border rounded-2xl p-6 ${wide ? "lg:col-span-2" : ""}`}>
      <div className="flex items-start gap-3 mb-4"><span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-emerald-50 text-emerald-brand"><Icon size={20} /></span><div><h2 className="font-heading font-bold">{title}</h2><p className="text-sm text-muted-foreground mt-0.5">{body}</p></div></div>
      {children}
    </div>
  );
}
